import { MutationPayload } from 'vuex';
import { Store as State } from './store';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Tracking, TrackingDefinition, TrackingFields, PKG_VERSION } from './AbstractTracking';

const PLUGININSTANCE_ID = 'wctcc3';
const LIFECYCLE_CHANNEL = `${PLUGININSTANCE_ID.toUpperCase()}-LIFECYCLE`;
const USA_EVENT_CATEGORY: EventCategory = 'eq_bc';

type EventType = 'click' | 'pageview' | 'global'; // defined in connection aem tracking from seamless
type EventAction = 'model_scroll' | 'model_select' | 'charge_eq';
type EventCategory = 'eq_bc';

interface UsaTrackingEvent {
  event: EventType;
  application: ApplicationData;
  page: PageData;
  eventDetails: EventDetails;
}

interface EventDetails {
  category: EventCategory;
  action: EventAction;
  label: string;
  vehicle: {
    modelName: string;
    baumusterId: string;
  };
}

interface PageData {
  name: string;
  vehicle: string;
}

interface ApplicationData {
  id: 'EQ';
  subID: 'CTC';
  version: string;
}

interface PreFillObject {
  category: EventCategory;
  event: EventType;
  action: EventAction;
  label: string;
  state: State;
}

const staticData: { application: ApplicationData; pageName: string } = {
  application: {
    id: 'EQ',
    subID: 'CTC',
    version: PKG_VERSION,
  },
  pageName: 'charging-calculator',
};

interface UsaTrackingFields extends TrackingFields {
  action: EventAction;
}
type UsaTrackingDefinition = TrackingDefinition<UsaTrackingFields>;
const TrackableEvents: UsaTrackingDefinition = {
  'chargingData/setSelectedModel': {
    label: 'model',
    alias: '',
    payloadPath: 'payload.className',
    action: 'model_scroll',
  },
  'chargingData/setSelectedVehicle': {
    label: 'vehicle',
    alias: '',
    payloadPath: 'payload.name',
    action: 'model_select',
  },
  'chargingData/setBatteryCharging': {
    label: 'BatteryChargingChanged',
    alias: 'bc:',
    payloadPath: 'payload',
    action: 'charge_eq',
  },
};

export class UsaTracking extends Tracking<UsaTrackingEvent, State, UsaTrackingDefinition> {
  private chargingCalculatorTracker: Subject<UsaTrackingEvent>;

  constructor() {
    super(TrackableEvents, LIFECYCLE_CHANNEL, PLUGININSTANCE_ID);
    this.chargingCalculatorTracker = new Subject<UsaTrackingEvent>();

    this.chargingCalculatorTracker.pipe(debounceTime(500)).subscribe((event) => {
      this.addEvent(event);
    });
  }

  public trackMiddleware = (mutation: MutationPayload, state: State): void => {
    const trackInfos = this.toTrackActions[mutation.type];
    if (trackInfos) {
      switch (mutation.type) {
        case 'chargingData/setBatteryCharging': {
          const value = this.getPayloadByStringDefinition<string>(mutation, trackInfos.payloadPath);
          const trackingEventWrapper = this.getPreFilledEvent({
            action: trackInfos.action,
            event: 'click',
            label: `${trackInfos.alias}${value}`,
            category: USA_EVENT_CATEGORY,
            state,
          });
          this.chargingCalculatorTracker.next(trackingEventWrapper);
          break;
        }
        default: {
          const trackingEventWrapper = this.getPreFilledEvent({
            action: trackInfos.action,
            event: 'click',
            label: `${trackInfos.alias}${this.getPayloadByStringDefinition(mutation, trackInfos.payloadPath)}`,
            category: USA_EVENT_CATEGORY,
            state,
          });
          this.addEvent(trackingEventWrapper);
          break;
        }
      }
    }
  };

  private getPreFilledEvent(input: PreFillObject): UsaTrackingEvent {
    return {
      application: staticData.application,
      page: {
        name: staticData.pageName,
        vehicle: input.state.chargingData.selectedVehicle?.name ?? '',
      },
      eventDetails: {
        vehicle: {
          modelName: input.state.chargingData.selectedVehicle?.name ?? '',
          // oneweb tracking needs the first 6 digits of the baumuster ID.
          // baumusterID is usually defined as:
          // digits 0-3: vehicle class (e.g. EQC)
          // digits 4-5: model (e.g. 400 4MATIC)
          // digit 6: model variant (e.g. AMG Edition)
          baumusterId: input.state.chargingData.selectedVehicle?.typeId.split('-')[0].substring(0, 6) ?? '',
        },
        action: input.action,
        label: input.label,
        category: input.category,
      },
      event: input.event,
    };
  }

  private addEvent(event: UsaTrackingEvent) {
    this.publishTrackEvent(event);
  }
}
